/**
 * The owner's controls on Members: who administers the organization.
 *
 * Kept out of `Organizations.tsx`, which is the seat list and the money, and
 * already long. Each control here is drawn **only for the owner** — the Worker
 * refuses everybody else (`requireOrgOwner`), and a row of links an
 * administrator cannot use would be a page lying about what they may do.
 */
import { useState } from 'react';
import type React from 'react';
import { color } from '../theme/tokens';
import { MenuButton } from '../components/MenuButton';
import { Modal } from '../components/Modal';
import { api, ApiError, apiPath } from '../lib/api';
import type { Organization, Seat } from '../lib/types';

/** What the seat routes answer: the organization and its people as they now stand. */
export interface SeatAnswer {
  organization: Organization | null;
  seats: Seat[];
}

/** Whether a row can be given or lose the administrator's role from this page. */
export function roleMovable(seat: Seat): boolean {
  return seat.role !== 'owner' && (seat.seatStatus === 'assigned' || seat.seatStatus === 'member');
}

/**
 * The role, and — for the owner, on anybody but themselves — the one word that
 * changes it. Nothing is asked first: the move is undone by the link that
 * replaces it, and it touches no seat and no money.
 */
export function RoleCell({
  orgId,
  seat,
  owner,
  onChanged,
  onError,
  onHandOver
}: {
  orgId: string;
  seat: Seat;
  /** Whether the person looking is this organization's owner. */
  owner: boolean;
  onChanged: (answer: SeatAnswer) => void;
  onError: (message: string) => void;
  /** Open the hand-over dialog for this person — it asks before anything moves. */
  onHandOver: (seat: Seat) => void;
}): React.JSX.Element {
  const [saved, setSaved] = useState(false);

  const change = async (role: 'member' | 'admin'): Promise<void> => {
    if (role === seat.role) return;
    try {
      onChanged(await api.patch<SeatAnswer>(apiPath`/orgs/${orgId}/seats/${seat.id}/role`, { role }));
      // Applied at once, said beside the cell for 1.6 s (§41.2).
      setSaved(true);
      window.setTimeout(() => setSaved(false), 1600);
    } catch (failure) {
      onError(failure instanceof ApiError ? failure.message : 'That did not work.');
    }
  };

  // Whoever cannot choose roles reads the role as text (§41.2).
  if (!owner || !roleMovable(seat)) {
    return <span style={{ color: color.inkSubtle, textTransform: 'capitalize' }}>{seat.role}</span>;
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
      <MenuButton
        label={seat.role === 'admin' ? 'Admin' : 'Member'}
        items={[
          { label: 'Member', checked: seat.role === 'member', onPick: () => void change('member') },
          { label: 'Admin', checked: seat.role === 'admin', onPick: () => void change('admin') },
          {
            label: 'Make owner',
            separated: true,
            onPick: () => onHandOver(seat),
            // Held before the name is typed, not refused after (proposal 18).
            reason: seat.confirmed === false ? `${seat.email} has not confirmed their address yet.` : undefined
          }
        ]}
      />
      {saved && <span style={{ fontSize: 12.5, color: color.okText, whiteSpace: 'nowrap' }}>✓ Saved</span>}
    </div>
  );
}

/**
 * Handing the organization over: the one act on this page its owner cannot take
 * back, so it asks for the organization's name typed out rather than a click.
 *
 * It says what moves and what does not, because the money is what people
 * worry about: the subscription, the card and the billing address stay; the
 * next invoice is filed under the new owner; the owner who hands it over stays
 * on as an administrator. A refusal — an unconfirmed address, somebody who
 * already owns an organization — lands in the dialog, with the name still typed.
 */
export function HandOver({
  orgId,
  orgName,
  seat,
  onDone,
  onCancel
}: {
  orgId: string;
  orgName: string;
  seat: Seat;
  onDone: (answer: SeatAnswer & { delivery: { sent: boolean } }) => void;
  onCancel: () => void;
}): React.JSX.Element {
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const confirm = async (): Promise<void> => {
    if (typed.trim().toLowerCase() !== orgName.trim().toLowerCase()) {
      setProblem(`Type ${orgName} exactly to hand it over.`);
      return;
    }
    setBusy(true);
    setProblem(null);
    try {
      onDone(
        await api.post<SeatAnswer & { delivery: { sent: boolean } }>(apiPath`/orgs/${orgId}/owner`, { seatId: seat.id, confirm: typed })
      );
    } catch (failure) {
      setProblem(failure instanceof ApiError ? failure.message : 'That did not work.');
      setBusy(false);
    }
  };

  return (
    <Modal
      title={`Make ${seat.email} the owner of ${orgName}?`}
      lede={`They become its owner and you stay on as an administrator — only they can undo this. The subscription, the card and the billing address stay as they are; invoices from today are filed under their account, and the earlier ones stay under yours. They must have confirmed their email address.`}
      label={`Type ${orgName} to confirm`}
      value={typed}
      placeholder={orgName}
      onChange={setTyped}
      confirm="Hand it over"
      danger
      busy={busy}
      problem={problem}
      onConfirm={() => void confirm()}
      onCancel={onCancel}
    />
  );
}
